import React from 'react'
import { Formik, Form, Field, ErrorMessage } from 'formik'
import * as Yup from 'yup'
import ReCAPTCHA from 'react-google-recaptcha'

import { Button, InputFile, P, Card } from '../../lib/components'
import { OpportunityInterestFormProps } from '../../lib/types/propsType_v1'

interface OpportunityInterestPresenterProps {
  initialValues: OpportunityInterestFormProps
  handleFormSubmit: (values: OpportunityInterestFormProps) => void
  reRef: React.RefObject<ReCAPTCHA>
  fileError: string
  selectedFile: File | null
  handleFileChange: (
    file: File,
    setFieldValue: (field: string, value: string | null) => void,
  ) => void
}

const RECAPTCHA_SITE_KEY =
  process.env.REACT_APP_SITE_KEY_V2 || '6LeIxAcTAAAAAJcZVRqyHh71UMIEGNQ_MXjiZKhI'

export const OpportunityInterestPresenter = ({
  initialValues,
  handleFormSubmit,
  reRef,
  fileError,
  selectedFile,
  handleFileChange,
}: OpportunityInterestPresenterProps) => {
  const validationSchema = Yup.object().shape({
    name: Yup.string().required('Name is required'),
    email: Yup.string().email('Invalid email address').required('Email is required'),
    phone: Yup.string().required('Phone number is required'),
    description: Yup.string()
      .min(100, 'Description must be at least 100 characters')
      .max(500, 'Description must not exceed 500 characters')
      .required('Description is required'),
  })

  return (
    <Card classes={['opportunity-interest-card']}>
      <div className='opportunity-interest-wrapper'>
        <Formik
          initialValues={initialValues}
          validationSchema={validationSchema}
          onSubmit={handleFormSubmit}
        >
          {({ isSubmitting, setFieldValue, values }) => (
            <Form className='opportunity-interest-form'>
              <div className='form-field'>
                <label htmlFor='name'>Name</label>
                <Field
                  type='text'
                  name='name'
                  id='name'
                  className='form-input'
                  placeholder='Your full name'
                />
                <ErrorMessage name='name' component='div' className='error-message' />
              </div>

              <div className='form-field'>
                <label htmlFor='email'>Work Email</label>
                <Field
                  type='email'
                  name='email'
                  id='email'
                  className='form-input'
                  placeholder='work@company.com'
                />
                <ErrorMessage name='email' component='div' className='error-message' />
              </div>

              <div className='form-field'>
                <label htmlFor='phone'>Phone Number</label>
                <Field
                  type='text'
                  name='phone'
                  id='phone'
                  className='form-input'
                  placeholder='Your phone number'
                />
                <ErrorMessage name='phone' component='div' className='error-message' />
              </div>

              <div className='form-field'>
                <label htmlFor='description'>Description of your opportunity</label>
                <Field
                  as='textarea'
                  name='description'
                  id='description'
                  className='form-textarea'
                  placeholder='Please describe your opportunity in detail'
                  rows={6}
                />
                <div className='character-counter'>
                  {values.description.length}/500 characters (minimum 100 characters required)
                </div>
                <ErrorMessage name='description' component='div' className='error-message' />
              </div>

              <div className='form-field'>
                <label>Pitch Deck</label>
                <InputFile
                  handleFiles={(file) => handleFileChange(file as File, setFieldValue)}
                  label='Upload Pitch Deck'
                  name='pitchDeck'
                  type='doc'
                  file={{ name: '' }}
                  errors={fileError ? { fileError: fileError } : {}}
                  touched={fileError ? { fileError: true } : {}}
                />
                {selectedFile && (
                  <div className='selected-file'>
                    <P text={`Selected file: ${selectedFile.name}`} classes={['file-name']} />
                  </div>
                )}
                {fileError && <div className='error-message'>{fileError}</div>}
              </div>

              <div className='recaptcha-container'>
                <ReCAPTCHA sitekey={RECAPTCHA_SITE_KEY} ref={reRef} />
              </div>

              <Button
                classes={['submit-button', 'mt-24']}
                text='Submit'
                variant='primary-btn'
                handleClick={() => {
                  /* Submit handled by Formik */
                }}
                isDisabled={isSubmitting || !!fileError}
                buttonType='submit'
              />
            </Form>
          )}
        </Formik>
      </div>
    </Card>
  )
}
